import classNames from "classnames";
import {
  ButtonHTMLAttributes,
  DetailedHTMLProps,
  ForwardedRef,
  forwardRef,
  MouseEvent,
  ReactNode,
} from "react";
import { Icon } from "../icon/icon";
import { LoadingOval } from "../loaders/loaders";

export type ButtonVariations =
  | "primary"
  | "plain"
  | "plain-grey"
  | "plain-grey-2"
  | "plain-grey-3"
  | "plain-danger"
  | "grey"
  | "danger"
  | "green-outline"
  | "white"
  | "bland"
  | "plain-text-grey"
  | "white-text-grey"
  | "disabled"
  | "styled-grey"
  | "social-plain-grey"
  | "blue"
  | "grey-800";

export type ButtonProps = {
  text: string | ReactNode;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  type: "button" | "submit";
  className?: string;
  loading?: boolean;
  disabled?: boolean;
  icon?: string;
  iconClassName?: string;
  variation?: ButtonVariations;
  size?: "small" | "regular" | "zero" | "smaller";
  loaderWidth?: string;
  loaderHeight?: string;
  iconFirst?: boolean;
  iconDimension?: number;
  extra?: ReactNode;
} & DetailedHTMLProps<
  ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
>;

// 👇 Use forwardRef
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      text,
      onClick,
      type = "submit",
      className = "",
      loading = false,
      disabled = false,
      icon = "",
      iconClassName = "",
      variation = "primary",
      size = "regular",
      loaderWidth = "25",
      iconFirst = true,
      loaderHeight = "25",
      iconDimension = 16,
      extra,
      ...otherProps
    },
    ref: ForwardedRef<HTMLButtonElement>,
  ) => {
    const getColors = () => {
      switch (variation) {
        case "primary":
          return "bg-primary border border-primary text-white";
        case "plain":
          return "bg-transparent border border-primary text-primary";
        case "plain-grey":
          return "bg-transparent border border-grey-950/10 text-primary";
        case "plain-grey-2":
          return "bg-transparent border border-grey-200/50 text-primary";
        case "social-plain-grey":
          return "bg-transparent border border-grey-200 text-grey-600";
        case "plain-text-grey":
          return "bg-transparent border border-grey-950/10 text-grey-800";
        case "white-text-grey":
          return "bg-white border border-white text-grey-800";
        case "plain-danger":
          return "bg-transparent border border-grey-950/10 text-danger";
        case "grey":
          return "bg-grey-50 border border-grey-50 text-primary";
        case "danger":
          return "bg-danger border border-danger text-white";
        case "green-outline":
          return "bg-transparent border border-success text-success";
        case "white":
          return "bg-white border border-white text-primary";
        case "disabled":
          return "bg-grey-100 border border-grey-100 text-grey-600";
        case "bland":
          return "bg-transparent border border-transparent text-grey-950";
        case "grey-800":
          return "bg-transparent border border-transparent text-grey-800";
        case "blue":
          return "bg-transparent border border-none text-blue-500";
        case "plain-grey-3":
          return "bg-grey-100 border border-none text-grey-950";
        case "styled-grey":
          return "hover:bg-grey-50 hover:border-grey-50 hover:text-primary active:bg-grey-50 active:border-grey-50 active:text-primary";
      }
    };

    const getLoaderColors = () => {
      return ["primary", "danger"].includes(variation) ? "#ffffff" : "#04000A";
    };

    return (
      <button
        {...otherProps}
        ref={ref}
        type={type}
        onClick={(e) => {
          if (onClick) {
            if (type !== "submit") e.preventDefault();
            onClick(e);
          }
        }}
        disabled={disabled || loading}
        className={classNames(
          `soft-shrink relative flex w-full items-center justify-center gap-2.5 rounded-[100px] text-base font-bold`,
          getColors(),
          {
            "px-5 py-3": size === "regular",
            "px-3 py-[10px]": size === "small",
            "px-2.5 py-[6px]": size === "smaller",
            "px-0 py-0": size === "zero",
            "!text-transparent": loading,
            "disabled:cursor-not-allowed disabled:!border-grey-200 disabled:!bg-grey-100 disabled:!text-grey-600 disabled:opacity-100 disabled:hover:scale-100":
              !loading,
          },
          className,
        )}
      >
        {icon && (
          <div
            className={`max-h-fit ${iconFirst ? "order-first" : "order-last"}`}
          >
            <Icon
              svg={icon}
              width={iconDimension}
              height={iconDimension}
              className={`${iconClassName}`}
            />
          </div>
        )}

        {text && <span>{text}</span>}

        {extra}

        {loading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <LoadingOval
              loaderHeight={loaderHeight}
              loaderWidth={loaderWidth}
              color={getLoaderColors()}
            />
          </div>
        )}
      </button>
    );
  },
);

Button.displayName = "Button"; // 👈 Required for forwardRef components
